import type { ReactNode } from "react";
import type { PermissionValues } from "../features/navigation/Permissions";
import { AccessSubscription, AvataxRole, EcmRole } from "../features/navigation/Subscription";
/**
 * Session-derived conditions a feature can be gated on. Each key is resolved by
 * `useSessionCheck`, which applies that key's own rule; the key alone is not decisive.
 */
export const SessionChecks = {
    VendorInheritance: "vendorInheritance",
} as const;

export type SessionCheck = (typeof SessionChecks)[keyof typeof SessionChecks];

/**
 * Individual feature flag configuration
 */
export interface FeatureFlagConfig {
    /** Unique identifier for the feature */
    name: string;

    /** Human-readable description of the feature */
    description: string;

    /** Array of subscriptions that have access to this feature */
    subscription?: AccessSubscription;

    /** Array of ECM roles that can access this feature */
    ecmRole?: EcmRole[];

    /** Array of AvaTax roles that can access this feature */
    avataxRole?: AvataxRole[];

    /** Whether admin check is required */
    checkAdmin?: boolean;

    /** Setting identifier that must be enabled for this feature */
    setting?: string;

    /** Array of permissions required for this feature */
    permissions?: PermissionValues;

    /** Whether any one of the permissions is sufficient (OR logic) */
    requireAnyPermission?: boolean;

    /** Whether this feature is for internal users only */
    internalUser?: boolean;

    /**
     * Environments in which the feature is restricted to internal users only.
     * In any environment not listed here, the internal-user restriction is not
     * applied (i.e. the feature is open to all eligible users).
     */
    internalUserEnv?: string[];

    /**
     * Session-derived condition that must allow this feature, resolved by `useSessionCheck`.
     * `vendorInheritance` blocks the feature for a company that inherits its parent's vendors.
     */
    sessionCheck?: SessionCheck;

    /** Whether this feature is protected */
    protected?: boolean;

    /** Array of environments where this feature should be available */
    env?: string[];
}

/**
 * Feature category containing multiple related features
 */
export interface FeatureCategory {
    [featureId: string]: FeatureFlagConfig;
}

/**
 * Complete feature flag configuration object
 */
export interface FeatureFlags {
    zoomInWidget: FeatureCategory;
    topNav: FeatureCategory;
    resyncCertificate: FeatureCategory;
    validatedCertificate: FeatureCategory;
    reviewCertificate: FeatureCategory;
    validatedSearchPage: FeatureCategory;
    reviewSearchPage: FeatureCategory;
    customers: FeatureCategory;
    companyGroups: FeatureCategory;
    settings: FeatureCategory;
    certificateFilters: FeatureCategory;
    requestCertificates: FeatureCategory;
    fvsUploader: FeatureCategory;
    vendors: FeatureCategory;
    vendorCertificates: FeatureCategory;
    provision: FeatureCategory;
    overview: FeatureCategory;
    reports: FeatureCategory;
    afcContent: FeatureCategory;
    retail: FeatureCategory;
    userRoleManagement: FeatureCategory;
    avatax: FeatureCategory;
    aiSearch: FeatureCategory;
    jobs: FeatureCategory;
    certificateAttachmentLabels: FeatureCategory;
    imports: FeatureCategory;
    managedService: FeatureCategory;
}

/**
 * Props for the FeatureToggler component
 */
export interface FeatureTogglerProps<T extends keyof FeatureFlags = keyof FeatureFlags> {
    /** Feature category name */
    category: T;

    /** Feature identifier within the category */
    id: keyof FeatureFlags[T];

    /** Children to render when feature is enabled */
    children: ReactNode;

    /** Fallback component to render when feature is disabled */
    fallback?: ReactNode;
}
